import React from 'react';
import Header from './header';
import Footer from './Footer';
import { ShieldCheck, RefreshCw, Mail, AlertTriangle } from 'lucide-react';
import './LegalPages.css';

const RefundPolicy: React.FC = () => {
  return (
    <div className="legal-page-wrapper">
      <Header />
      <div className="legal-container">
        <div className="legal-content-card">
          <div className="legal-header">
            <h1>Refund Policy</h1>
            <span className="last-updated-badge">Last Updated: July 16, 2026</span>
          </div>

          {/* Paddle Merchant of Record Callout */}
          <div className="mor-notice-banner">
            <ShieldCheck size={24} className="mor-icon" />
            <p>
              <strong>Merchant of Record Notice:</strong> Our order process is conducted by our online reseller <strong>Paddle.com</strong>. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.
            </p>
          </div>

          <div className="legal-body">
            <p className="lead fw-semibold" style={{ color: '#1e293b' }}>
              This Refund Policy applies to Service Provider subscription fees paid through SkillSeek and processed by our merchant of record, Paddle. It should be read alongside our <a href="/terms">Terms and Conditions</a> and <a href="/privacy">Privacy Policy</a>.
            </p>

            <div className="legal-highlight-box d-flex align-items-start gap-2">
              <AlertTriangle size={20} className="flex-shrink-0 mt-1" />
              <div>
                <strong>Non-Facilitation Notice:</strong> This Policy does not cover payment for services rendered by a Service Provider to a Customer. As explained in our Terms and Conditions, SkillSeek does not process, hold, or facilitate any payment between Customers and Service Providers — that payment is arranged and settled directly between the two parties, outside the Platform, and any refund of it must be requested directly from the Service Provider.
              </div>
            </div>

            <div className="legal-scroll-container">
              {/* Section 1 */}
              <div className="legal-section">
                <h2>1. What This Policy Covers</h2>
                <p>1.1 This Policy governs refund eligibility for the recurring subscription fee a Service Provider pays SkillSeek in exchange for the right to list and advertise their skills on the Platform (see Terms and Conditions, Section 6).</p>
                <p>1.2 All subscription payments are processed by Paddle, acting as merchant of record. Paddle handles the mechanics of issuing any approved refund.</p>
              </div>

              {/* Section 2 */}
              <div className="legal-section">
                <h2>2. General Refund Eligibility</h2>
                <p>2.1 Subscription fees are billed in advance for each billing cycle and are generally non-refundable once a billing cycle has started, except as set out below or as required by applicable law.</p>
                <p>2.2 We may, at our discretion, approve a refund in the following circumstances: (a) a duplicate or erroneous charge caused by a technical error on our end; (b) a subscription charge made after a cancellation request was submitted but not correctly processed; (c) an unauthorized transaction, subject to verification.</p>
                <p>2.3 Refunds are not provided for: (a) partial billing periods after a Service Provider cancels mid-cycle (cancellation takes effect at the end of the current cycle — see Section 3); (b) dissatisfaction with the volume, quality, or outcome of Customer inquiries received, since SkillSeek does not guarantee bookings, leads, or business outcomes; (c) failure to use the Platform during a paid billing cycle; (d) accounts suspended or terminated for violation of our Terms and Conditions.</p>
                <p>2.4 Where a Service Provider is located in a jurisdiction with statutory withdrawal or cooling-off rights that exceed this Policy, those broader statutory rights apply and take precedence.</p>
              </div>

              {/* Section 3 */}
              <div className="legal-section">
                <h2>3. Cancellations (Not the Same as Refunds)</h2>
                <p>3.1 A Service Provider may cancel their subscription at any time through their account settings or Paddle's customer portal.</p>
                <p>3.2 Cancellation stops future billing but does not retroactively refund the current billing period — the Service Provider retains access to paid features through the end of the period already paid for, unless a refund is separately approved under Section 2.</p>
              </div>

              {/* Section 4 */}
              <div className="legal-section">
                <h2>4. How to Request a Refund</h2>
                <p>4.1 Refund requests should be submitted to <a href="mailto:skillseekja@gmail.com">skillseekja@gmail.com</a> within 14 days of the charge in question.</p>
                <p>4.2 Please include your account email, the date of the charge, and the reason for your request.</p>
                <p>4.3 We aim to respond to refund requests within 5 business days. Approved refunds are processed by Paddle and may take 5–10 business days to appear on your original payment method, depending on your bank or card issuer.</p>
              </div>

              {/* Section 5 */}
              <div className="legal-section">
                <h2>5. Tier Changes and Proration</h2>
                <p>5.1 If a Service Provider upgrades to a higher subscription tier (e.g., by adding skills), the price difference may be charged immediately on a prorated basis or applied at the next billing cycle, as configured at checkout — this is not a refund event.</p>
                <p>5.2 If a Service Provider downgrades to a lower tier, the new (lower) price takes effect from the next billing cycle; we do not refund the difference for the current cycle already paid.</p>
              </div>

              {/* Section 6 */}
              <div className="legal-section">
                <h2>6. Chargebacks</h2>
                <p>6.1 If you believe you were charged in error, please contact us first at <a href="mailto:skillseekja@gmail.com">skillseekja@gmail.com</a> so we can resolve it directly — this is faster than a bank chargeback and avoids any account restrictions.</p>
                <p>6.2 Initiating a chargeback without first contacting us may result in suspension of your Service Provider account while the matter is investigated with Paddle.</p>
              </div>

              {/* Section 7 */}
              <div className="legal-section">
                <h2>7. Changes to This Policy</h2>
                <p>7.1 We may update this Refund Policy from time to time. Material changes will be reflected in the "Last Updated" date above and, where required, communicated to active subscribers in advance of taking effect.</p>
                <p>7.2 The version of this Policy in effect at the time of your charge governs that charge.</p>
              </div>

              {/* Contact Us */}
              <div className="contact-card">
                <h3 className="m-0 mb-2 d-flex align-items-center gap-2">
                  <Mail size={18} /> Contact Us
                </h3>
                <p>For refund inquiries or subscription billing support, please contact us at:</p>
                <p>Email: <a href="mailto:skillseekja@gmail.com">skillseekja@gmail.com</a></p>
                <p>Reseller & Merchant of Record: Paddle.com Market Limited</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RefundPolicy;
